import React from "react";
import { Box, FormControl, FormLabel, Option, Stack, Tooltip } from "@mui/joy";
import CommonModal from "../../common/CommonModal";
import DarkInput from "../../common/DarkInput";
import DarkSelect from "../../common/DarkSelect";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import SearchInputWithDropdown from "../../common/SearchInputWithDropdown";
import ProfileForm from "../../shared/profileForm/ProfileForm";
import { SUserAdd } from "../../../styles/StyledMUI";
import { colorTokens } from "../../../styles/globalStyles";
import { useCustomer } from "../../../contexts/api";
import useAppSettings from "../../../contexts/app/appSettings";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import type { RentalCreateRequest, RentalProduct } from "../../../api/rental/Rental.typed";
import { formatPrice, htToTtc } from "../../billing/shared/billingUtils";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (req: RentalCreateRequest) => Promise<unknown>;
  products: RentalProduct[];
};

function customerLabel(c: ZekayCustomer): string {
  const parts = [c.FirstName, c.LastName].filter(Boolean).join(" ").trim();
  return parts || `#${c.Id ?? "?"}`;
}

function emptyCustomerDraft(): ZekayCustomer {
  return {
    FirstName: "",
    LastName: "",
    Birthdate: undefined as unknown as Date,
    Street: undefined,
    PostalCode: undefined,
    City: undefined,
    Phonenumber: undefined,
    MailAddress: undefined,
    GdprConsent: false,
    Photo: undefined,
    MedicalCertificate: undefined,
  };
}

export default function RentalCreateModal({ open, onClose, onSubmit, products }: Props) {
  const { searchCustomers } = useCustomer();
  const { config } = useAppSettings();
  const { hasPermission } = usePermissions();
  const isDesync = config?.CustomersSync === false;
  const canCreateCustomer =
    isDesync &&
    (hasPermission(Permission.CUSTOMERS_PROFILE_EDIT) || hasPermission(Permission.CUSTOMERS_EDIT));
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState<ZekayCustomer[]>([]);
  const [customer, setCustomer] = React.useState<ZekayCustomer | null>(null);
  const [productId, setProductId] = React.useState<number | undefined>(undefined);
  const [notes, setNotes] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [createDraft, setCreateDraft] = React.useState<ZekayCustomer | undefined>(undefined);

  React.useEffect(() => {
    if (!open) {
      setSearch("");
      setResults([]);
      setCustomer(null);
      setProductId(undefined);
      setNotes("");
      setCreateDraft(undefined);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    if (customer) return;

    const trimmed = search.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      const r = await searchCustomers({ Query: trimmed });
      if (!cancelled) setResults(r ?? []);
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, search, customer, searchCustomers]);

  const handleConfirm = async () => {
    if (!customer?.Id) return;
    setSubmitting(true);
    await onSubmit({
      customer_id: customer.Id,
      rental_product_id: productId ?? null,
      notes: notes.trim() || undefined,
    });
    setSubmitting(false);
    onClose();
  };

  const isValid = customer?.Id != null;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Nouvelle location"
      size="md"
      fitContentHeight
      onConfirm={handleConfirm}
      confirmLabel="Créer"
      submitDisabled={!isValid}
      loading={submitting}
    >
      <Stack spacing={2}>
        <FormControl required>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Client</FormLabel>
          {customer ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1,
                borderRadius: "sm",
                border: `1px solid ${colorTokens.border.default}`,
                backgroundColor: colorTokens.bg.surfaceMuted,
              }}
            >
              <DarkTypography level="body-md">{customerLabel(customer)}</DarkTypography>
              <DarkTypography
                level="body-xs"
                colorVariant="muted"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => {
                  setCustomer(null);
                  setSearch("");
                }}
              >
                Changer
              </DarkTypography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SearchInputWithDropdown
                  value={search}
                  onChange={setSearch}
                  placeholder="Rechercher un client…"
                  results={results
                    .filter((c): c is ZekayCustomer & { Id: number } => c.Id != null)
                    .map((c) => ({ id: c.Id, label: customerLabel(c) }))}
                  onSelect={(item) => {
                    const c = results.find((r) => r.Id === item.id);
                    if (c) {
                      setCustomer(c);
                      setSearch("");
                      setResults([]);
                    }
                  }}
                  maxResults={10}
                />
              </Box>
              {canCreateCustomer && (
                <Tooltip title="Créer un client">
                  <IconButtonCTA
                    aria-label="Créer un client"
                    ctaVariant="accent"
                    size="sm"
                    onClick={() => setCreateDraft(emptyCustomerDraft())}
                  >
                    <SUserAdd />
                  </IconButtonCTA>
                </Tooltip>
              )}
            </Box>
          )}
        </FormControl>

        <FormControl>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Kit (optionnel)</FormLabel>
          <DarkSelect
            value={productId ?? ""}
            placeholder="Sans kit — location à l'unité"
            onChange={(_, v) => {
              if (v === "" || v == null) {
                setProductId(undefined);
                return;
              }
              setProductId(v as number);
            }}
          >
            <Option value="">Sans kit — location à l'unité</Option>
            {products
              .filter((p) => p.is_active)
              .map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.name} — {formatPrice(htToTtc(p.price_ht, p.tax_rate), p.currency)} TTC/jour
                </Option>
              ))}
          </DarkSelect>
        </FormControl>

        <FormControl>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Notes</FormLabel>
          <DarkInput value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormControl>
      </Stack>

      {createDraft != null && (
        <ProfileForm
          customer={createDraft}
          setProfileFormOpen={(value) => {
            const next = typeof value === "function" ? value(false) : value;
            if (!next) setCreateDraft(undefined);
          }}
          setCustomerUpdated={() => undefined}
          editable
          desync={isDesync}
          closeAfterCreate
          onCustomerCreated={(created) => {
            if (created.Id != null) {
              setCustomer(created);
              setSearch("");
              setResults([]);
            }
          }}
        />
      )}
    </CommonModal>
  );
}
